import React from "react";
import {
  Box,
  Card,
  Chip,
  Grid,
  LinearProgress,
  Sheet,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import Diversity3RoundedIcon from "@mui/icons-material/Diversity3Rounded";
import { useTheme } from "@mui/joy/styles";

import { useDonationsStore } from "../../store/useDonationsStore";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

function getFinancialYearLabel(date: Date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const startYear = month >= 3 ? year : year - 1;
  const endYear = startYear + 1;
  return `${startYear}-${String(endYear).slice(-2)}`;
}

export default function DonationSummary() {
  const { donations, loading } = useDonationsStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const summaryBackground = isDark
    ? "linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)"
    : "linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 255, 0.96) 100%)";
  const summaryOverlay = isDark
    ? "radial-gradient(70% 120% at 110% 0%, rgba(56, 189, 248, 0.16) 0%, transparent 70%), radial-gradient(80% 60% at 0% 110%, rgba(251, 191, 36, 0.14) 0%, transparent 75%)"
    : "radial-gradient(70% 120% at 110% 0%, rgba(255, 214, 153, 0.18) 0%, transparent 70%), radial-gradient(80% 60% at 0% 110%, rgba(175, 205, 255, 0.18) 0%, transparent 75%)";
  const smallCardBackground = isDark
    ? "linear-gradient(160deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.96))"
    : undefined;
  const tipBackground = isDark
    ? "linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.96))"
    : "linear-gradient(135deg, rgba(238, 241, 250, 0.9), rgba(246, 248, 252, 0.95))";

  const stats = React.useMemo(() => {
    if (!donations || donations.length === 0) {
      return {
        totalAmount: 0,
        thisMonthAmount: 0,
        offeringsThisWeek: 0,
        largestOffering: 0,
        uniqueDevotees: 0,
        latestOffering: undefined as Date | undefined,
        currentFinancialYear: getFinancialYearLabel(new Date()),
      };
    }

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    const donorSet = new Set<string>();

    let totalAmount = 0;
    let thisMonthAmount = 0;
    let offeringsThisWeek = 0;
    let largestOffering = 0;
    let latestOffering: Date | undefined;

    donations.forEach((donation) => {
      const amount = Number(donation.amount ?? 0);
      const donationDate = donation.date ? new Date(donation.date) : undefined;

      totalAmount += amount;
      if (donationDate) {
        if (donationDate >= startOfMonth) {
          thisMonthAmount += amount;
        }
        if (donationDate >= sevenDaysAgo) {
          offeringsThisWeek += 1;
        }
        if (!latestOffering || donationDate > latestOffering) {
          latestOffering = donationDate;
        }
      }

      if (amount > largestOffering) {
        largestOffering = amount;
      }

      const identity = donation.email || donation.phone || donation.name || String(donation.id);
      donorSet.add(identity);
    });

    return {
      totalAmount,
      thisMonthAmount,
      offeringsThisWeek,
      largestOffering,
      uniqueDevotees: donorSet.size,
      latestOffering,
      currentFinancialYear: donations[0]?.date
        ? getFinancialYearLabel(new Date(donations[0].date))
        : getFinancialYearLabel(new Date()),
    };
  }, [donations]);

  const monthlyGoal = 100000; // ₹1,00,000 goal as inspirational benchmark
  const monthlyProgress = Math.min(100, (stats.thisMonthAmount / monthlyGoal) * 100 || 0);

  return (
    <Grid container spacing={2.5} alignItems="stretch">
      <Grid xs={12} lg={8}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: "xl",
            p: { xs: 3, md: 4 },
            position: "relative",
            overflow: "hidden",
            height: "100%",
            borderColor: "divider",
            boxShadow: "sm",
            background: summaryBackground,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: summaryOverlay,
              pointerEvents: "none",
            }}
          />
          <Stack spacing={2.5} sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
              <Chip
                size="sm"
                variant="soft"
                color="primary"
                startDecorator={<CalendarTodayRoundedIcon fontSize="small" />}
                sx={{ fontWeight: 600 }}
              >
                Financial year {stats.currentFinancialYear}
              </Chip>
              {stats.latestOffering ? (
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Last offering received on {stats.latestOffering.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                </Typography>
              ) : null}
            </Stack>

            <Stack spacing={1.2}>
              <Typography level="h1" sx={{ fontWeight: 800, letterSpacing: -0.6 }}>
                {currencyFormatter.format(stats.totalAmount)}
              </Typography>
              <Typography level="body-lg" sx={{ color: "text.secondary", maxWidth: 520 }}>
                Collective generosity from {stats.uniqueDevotees} devotees is powering prasadam distribution, outreach, and seva initiatives.
              </Typography>
            </Stack>

            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FavoriteRoundedIcon fontSize="small" />
                <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                  This month’s offering progress
                </Typography>
              </Stack>
              <LinearProgress determinate value={monthlyProgress} sx={{ height: 10, borderRadius: 999 }} />
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                {currencyFormatter.format(stats.thisMonthAmount)} raised of our ₹1,00,000 devotional goal for the month.
              </Typography>
            </Stack>
          </Stack>
        </Card>
      </Grid>

      <Grid xs={12} lg={4}>
        <Stack spacing={1.5} sx={{ height: "100%" }}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: 3,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              background: smallCardBackground,
            }}
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <VolunteerActivismRoundedIcon color="primary" />
              <Typography level="title-md" sx={{ fontWeight: 700 }}>
                This week’s seva highlights
              </Typography>
            </Stack>
            {loading && donations.length === 0 ? (
              <Stack spacing={1}>
                <Skeleton level="body-lg" />
                <Skeleton level="body-sm" />
              </Stack>
            ) : (
              <>
                <Typography level="h3" sx={{ fontWeight: 700 }}>
                  {stats.offeringsThisWeek} offerings
                </Typography>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Every fresh contribution in the last seven days keeps our temple vibrant and welcoming.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip variant="soft" color="primary" size="sm">
                    Largest offering {currencyFormatter.format(stats.largestOffering)}
                  </Chip>
                </Stack>
              </>
            )}
          </Card>

          <Card
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              background: smallCardBackground,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Diversity3RoundedIcon color="primary" />
              <Typography level="title-sm" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                Devotee participation
              </Typography>
            </Stack>
            <Typography level="h3" sx={{ fontWeight: 700 }}>
              {stats.uniqueDevotees}
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Devotees who offered their heartfelt support across the database.
            </Typography>
          </Card>

          <Sheet
            variant="soft"
            color="neutral"
            sx={{
              borderRadius: "xl",
              p: 2.5,
              border: "1px solid",
              borderColor: "divider",
              background: tipBackground,
            }}
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <EmojiEventsRoundedIcon color="primary" />
              <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                Tip
              </Typography>
            </Stack>
            <Typography level="body-sm" sx={{ mt: 1 }}>
              Record every donation promptly so devotees receive timely receipts and blessings.
            </Typography>
          </Sheet>
        </Stack>
      </Grid>
    </Grid>
  );
}
